import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus,
  X,
  ArrowRight,
  Loader2,
  History,
  Target,
  Search,
  Tag,
  Store,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useApi } from "../hooks/useApi";
import { DashboardLayout } from "../components/DashboardLayout";
import { ListingCard } from "../components/market/ListingCard";
import { BidManagement } from "../components/market/BidManagement";
import { BuyerRequestsList } from "../components/market/BuyerRequestsList";

export const MarketPage = () => {
  const api = useApi();
  const { user } = useAuth();
  const [tab, setTab] = useState("browse");
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [buyerRequests, setBuyerRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showListingModal, setShowListingModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [bidListing, setBidListing] = useState(null);
  const [bidsByListing, setBidsByListing] = useState({});
  const isBusiness = user?.account_type === "business";

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "browse") {
        const res = await api.get("/marketplace/listings");
        setListings(res.data);
      } else if (tab === "my-listings") {
        const res = await api.get("/marketplace/my-listings");
        setMyListings(res.data);
      } else if (tab === "requests") {
        const res = await api.get("/marketplace/requests");
        setBuyerRequests(res.data);
      } else if (tab === "my-requests") {
        const res = await api.get("/marketplace/my-requests");
        setMyRequests(res.data);
      } else if (tab === "history") {
        const res = await api.get("/transactions/history");
        setTransactions(res.data);
      }
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [api, tab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePurchase = async (listingId) => {
    if (!window.confirm("Confirm purchase? (Mock payment)")) return;
    try {
      const res = await api.post(`/marketplace/purchase/${listingId}`);
      toast.success(`Purchase successful! Compliance certificate ${res.data.certificate_number} issued.`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purchase failed");
    }
  };

  const handleRemoveListing = async (listingId) => {
    if (!window.confirm("Remove this listing?")) return;
    try {
      await api.delete(`/marketplace/listings/${listingId}`);
      toast.success("Listing removed");
      loadData();
    } catch (e) {
      toast.error("Failed to remove listing");
    }
  };

  const loadBids = async (listingId) => {
    try {
      const res = await api.get(`/marketplace/listings/${listingId}/bids`);
      setBidsByListing((prev) => ({ ...prev, [listingId]: res.data }));
    } catch (e) {
      toast.error("Failed to load bids");
    }
  };

  const handleAcceptBid = async (bidId) => {
    if (!window.confirm("Accept this bid and complete the sale?")) return;
    try {
      const res = await api.post(`/marketplace/bids/${bidId}/accept`);
      toast.success(`Sale complete! Certificate ${res.data.certificate_number} issued.`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept bid");
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="market-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Data Marketplace</h1>
            <p className="text-gray-400 mt-1">Buy, sell, and trade personal data</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowListingModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-cyan-500 text-white rounded-xl font-medium hover:shadow-lg transition"
            >
              <Plus className="w-4 h-4" /> List Data
            </button>
            <button
              onClick={() => setShowRequestModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20 transition border border-purple-500/30"
            >
              <Target className="w-4 h-4" /> Post Request
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: "browse", label: "Browse Listings", icon: Store },
            { id: "requests", label: "Buyer Requests", icon: Target },
            { id: "my-listings", label: "My Listings", icon: Tag },
            { id: "my-requests", label: "My Requests", icon: Search },
            { id: "history", label: "Transaction History", icon: History },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
                tab === t.id
                  ? "bg-gradient-to-r from-purple-600 to-cyan-500 text-white"
                  : "bg-white/10 text-gray-400 hover:bg-white/20 border border-purple-500/20"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : (
          <>
            {/* Browse Listings */}
            {tab === "browse" && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {listings.length === 0 ? (
                  <div className="col-span-full bg-white/5 rounded-2xl p-12 text-center border border-purple-500/20">
                    <Store className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No listings available</p>
                  </div>
                ) : (
                  listings.map((listing) => (
                    <ListingCard
                      key={listing.id}
                      listing={listing}
                      isBusiness={isBusiness}
                      currentUserId={user?.id}
                      onBid={setBidListing}
                      onPurchase={handlePurchase}
                    />
                  ))
                )}
              </div>
            )}

            {/* Buyer Requests */}
            {tab === "requests" && <BuyerRequestsList requests={buyerRequests} />}

            {/* My Listings */}
            {tab === "my-listings" && (
              <div className="space-y-4">
                {myListings.length === 0 ? (
                  <div className="bg-white/5 rounded-2xl p-12 text-center border border-purple-500/20">
                    <Tag className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">You haven't listed anything yet</p>
                  </div>
                ) : (
                  myListings.map((listing) => (
                    <BidManagement
                      key={listing.id}
                      listing={listing}
                      bids={bidsByListing[listing.id]}
                      onLoadBids={loadBids}
                      onRemove={handleRemoveListing}
                      onAccept={handleAcceptBid}
                    />
                  ))
                )}
              </div>
            )}

            {/* My Requests */}
            {tab === "my-requests" && (
              <div className="space-y-4">
                {myRequests.length === 0 ? (
                  <div className="bg-white/5 rounded-2xl p-12 text-center border border-purple-500/20">
                    <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">You haven't posted any requests yet</p>
                  </div>
                ) : (
                  myRequests.map((req) => (
                    <div key={req.id} className="bg-white/5 rounded-2xl p-6 border border-purple-500/20 flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-white">{req.title}</h3>
                        <p className="text-sm text-gray-400">Budget: ${req.max_budget} • {req.responses} responses • {req.status}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        req.status === 'open' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {req.status}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Transaction History */}
            {tab === "history" && (
              <div className="space-y-4">
                {transactions.length === 0 ? (
                  <div className="bg-white/5 rounded-2xl p-12 text-center border border-purple-500/20">
                    <History className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No transactions yet</p>
                  </div>
                ) : (
                  transactions.map((tx) => (
                    <div key={tx.id} className="bg-white/5 rounded-2xl p-6 border border-purple-500/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            tx.role === 'seller' ? 'bg-green-500/20' : 'bg-cyan-500/20'
                          }`}>
                            {tx.role === 'seller' ? (
                              <ArrowRight className="w-5 h-5 text-green-400 rotate-45" />
                            ) : (
                              <ArrowRight className="w-5 h-5 text-cyan-400 -rotate-135" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-white">{tx.listing_title}</p>
                            <p className="text-sm text-gray-400">
                              {tx.role === 'seller' ? `Sold to ${tx.other_party}` : `Bought from ${tx.other_party}`}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${tx.role === 'seller' ? 'text-green-400' : 'text-cyan-400'}`}>
                            {tx.role === 'seller' ? '+' : '-'}${tx.amount.toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-500">{new Date(tx.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

        {/* Create Listing Modal */}
        {showListingModal && (
          <CreateListingModal onClose={() => { setShowListingModal(false); loadData(); }} />
        )}

        {/* Create Request Modal */}
        {showRequestModal && (
          <CreateRequestModal onClose={() => { setShowRequestModal(false); loadData(); }} />
        )}

        {/* Place Bid Modal */}
        {bidListing && (
          <BidModal listing={bidListing} onClose={() => setBidListing(null)} onPlaced={() => { setBidListing(null); loadData(); }} />
        )}
      </div>
    </DashboardLayout>
  );
};

// Bid Modal (business buyers place a bid on a listing)
export const BidModal = ({ listing, onClose, onPlaced }) => {
  const api = useApi();
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/marketplace/listings/${listing.id}/bids`, {
        amount: parseFloat(amount),
        message: message || null,
      });
      toast.success("Bid placed!");
      onPlaced();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to place bid");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl p-6 w-full max-w-md border border-purple-500/30" onClick={(e) => e.stopPropagation()} data-testid="bid-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Place a Bid</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-400 mb-4">{listing.title} • asking ${listing.price.toFixed(2)}</p>
        <form onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-gray-300 mb-2">Your Bid ($)</label>
          <input
            type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 outline-none text-white mb-4"
            placeholder="65.00" required data-testid="bid-amount-input"
          />
          <label className="block text-sm font-medium text-gray-300 mb-2">Message (optional)</label>
          <textarea
            value={message} onChange={(e) => setMessage(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 outline-none text-white mb-4"
            placeholder="Intended use of this data..." rows={2} data-testid="bid-message-input"
          />
          <button type="submit" disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="submit-bid-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit Bid"}
          </button>
        </form>
      </div>
    </div>
  );
};

// Create Listing Modal
export const CreateListingModal = ({ onClose }) => {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({
    data_item_id: "",
    title: "",
    description: "",
    price: "",
  });
  const [loading, setLoading] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      const res = await api.get("/data");
      setItems(res.data.filter(item => !item.is_for_sale));
    } catch (e) {
      toast.error("Failed to load items");
    }
  }, [api]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/marketplace/listings", {
        ...formData,
        price: parseFloat(formData.price),
      });
      toast.success("Listing created!");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create listing");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl p-6 w-full max-w-lg border border-purple-500/20">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Create Listing</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Select Data Item</label>
            <select
              value={formData.data_item_id}
              onChange={(e) => setFormData({ ...formData, data_item_id: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 outline-none text-white"
              required
            >
              <option value="" className="bg-slate-900">Select an item</option>
              {items.map((item) => (
                <option key={item.id} value={item.id} className="bg-slate-900">
                  {item.name} (${item.estimated_value?.toFixed(2)})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Listing Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 outline-none text-white"
              placeholder="e.g., Premium Shopping Data"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 outline-none text-white h-24 resize-none"
              placeholder="Describe what buyers will get..."
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Price ($)</label>
            <input
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 outline-none text-white"
              placeholder="25.00"
              min="0.01"
              step="0.01"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-cyan-500 text-white rounded-xl font-medium hover:shadow-lg transition disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Create Listing"}
          </button>
        </form>
      </div>
    </div>
  );
};

// Create Request Modal
export const CreateRequestModal = ({ onClose }) => {
  const api = useApi();
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    category: "",
    title: "",
    description: "",
    max_budget: "",
  });
  const [loading, setLoading] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get("/categories");
      setCategories(res.data);
    } catch (e) {
      console.error("Failed to load categories");
    }
  }, [api]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/marketplace/requests", {
        ...formData,
        max_budget: parseFloat(formData.max_budget),
      });
      toast.success("Request posted!");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl p-6 w-full max-w-lg border border-purple-500/20">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Post Buyer Request</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Data Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 outline-none text-white"
              required
            >
              <option value="" className="bg-slate-900">Select a category</option>
              {categories.map((cat) => (
                <option key={cat.name} value={cat.name} className="bg-slate-900 capitalize">
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Request Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 outline-none text-white"
              placeholder="e.g., Looking for fitness tracking data"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 outline-none text-white h-24 resize-none"
              placeholder="Describe what kind of data you're looking for..."
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Maximum Budget ($)</label>
            <input
              type="number"
              value={formData.max_budget}
              onChange={(e) => setFormData({ ...formData, max_budget: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 outline-none text-white"
              placeholder="50.00"
              min="1"
              step="0.01"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl font-medium hover:shadow-lg transition disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Post Request"}
          </button>
        </form>
      </div>
    </div>
  );
};


// Analytics Page
